import { Dispatch, SetStateAction, useMemo } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SpinnerBlock from 'components/others/SpinnerBlock';
import spinnerTitle from 'components/others/spinnerTitle';
import SourceSwitches from './SourceSwitches';
import ProductsTransferTable from './ProductsTransferTable';
import ServicesTransferTable from './ServicesTransferTable';
import ProductsStockAlertBanner from './ProductsStockAlertBanner';
import useProposal2Warehouse from './model/useProposal2Warehouse';
import { getProductsStockAlert } from './model/getProductsStockAlert';
import type { Proposal2WarehouseTarget, WarehouseSourceSwitchKey } from './model/types';
import SolarIcon from 'components/icons/SolarIcon';

const SWITCH_SOURCE_KEYS: WarehouseSourceSwitchKey[] = ['reserve', 'team', 'custom', 'main'];

export type { Proposal2WarehouseTarget };

interface Proposal2WarehouseProps {
    showTarget: Proposal2WarehouseTarget | null;
    setShowTarget: Dispatch<SetStateAction<Proposal2WarehouseTarget | null>>;
}

const Proposal2Warehouse = ({ showTarget, setShowTarget }: Proposal2WarehouseProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const ns = 'documents.proposal2Warehouse';
    const {
        step,
        title,
        sourceOptions,
        switchLabels,
        switchHints,
        useReserve,
        useTeam,
        useCustom,
        useMain,
        addServices,
        skipExisting,
        setUseReserve,
        setUseTeam,
        setUseCustom,
        setUseMain,
        setAddServices,
        setSkipExisting,
        hasAdditionalItems,
        productRows,
        serviceRows,
        forming,
        approving,
        canApprove,
        toggleProduct,
        toggleService,
        handleForm,
        handleApprove,
        handleBackToSources,
        handleHide,
    } = useProposal2Warehouse({ showTarget, setShowTarget });

    const busy = forming || approving;
    const productsAlert = useMemo(
        () => getProductsStockAlert(productRows),
        [productRows],
    );

    return (
        <Offcanvas
            className="off-import"
            show={showTarget !== null}
            onHide={handleHide}
            placement="end"
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>{title || t(`${ns}.title`)}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 pt-0 d-flex flex-column px-0">
                <div className="off-transfer-form d-flex flex-column h-100">
                    <div className="off-transfer-content">
                        <SpinnerBlock onlyBlur={true} show={busy} />

                        {step === 'sources' ? (
                            <SourceSwitches
                                sourceOptions={sourceOptions.filter((s): s is typeof s & { key: WarehouseSourceSwitchKey } =>
                                    SWITCH_SOURCE_KEYS.includes(s.key as WarehouseSourceSwitchKey),
                                )}
                                switchLabels={switchLabels}
                                switchHints={switchHints}
                                useReserve={useReserve}
                                useTeam={useTeam}
                                useCustom={useCustom}
                                useMain={useMain}
                                addServices={addServices}
                                skipExisting={skipExisting}
                                hasAdditionalItems={hasAdditionalItems}
                                disabled={busy}
                                sourcesTitle={t(`${ns}.sourcesTitle`)}
                                switchServicesLabel={t(`${ns}.switchServices`)}
                                switchServicesHint={t(`${ns}.switchServicesHint`)}
                                switchSkipExistingLabel={t(`${ns}.switchSkipExisting`)}
                                switchSkipExistingHint={t(`${ns}.switchSkipExistingHint`)}
                                onToggleReserve={setUseReserve}
                                onToggleTeam={setUseTeam}
                                onToggleCustom={setUseCustom}
                                onToggleMain={setUseMain}
                                onToggleServices={setAddServices}
                                onToggleSkipExisting={setSkipExisting}
                            />
                        ) : null}

                        {step === 'preview' || step === 'result' ? (
                            <>
                                <ProductsStockAlertBanner level={productsAlert.level} />
                                {productRows.length ? (
                                    <ProductsTransferTable
                                        rows={productRows}
                                        mode={step === 'result' ? 'result' : 'preview'}
                                        onToggle={step === 'preview' ? toggleProduct : undefined}
                                    />
                                ) : null}
                                {serviceRows.length ? (
                                    <ServicesTransferTable
                                        rows={serviceRows}
                                        mode={step === 'result' ? 'result' : 'preview'}
                                        onToggle={step === 'preview' ? toggleService : undefined}
                                    />
                                ) : null}
                            </>
                        ) : null}
                    </div>

                    <div className="p-3 offcanvas-footer off-transfer-footer d-flex justify-content-end gap-2 flex-wrap">
                        {step === 'sources' ? (
                            <Button
                                variant="info"
                                className="min100px off-transfer-footer-btn"
                                disabled={busy}
                                onClick={() => void handleForm()}
                            >
                                {spinnerTitle(t(`${ns}.btnForm`), forming, t(`${ns}.btnForm`))}
                            </Button>
                        ) : null}

                        {step === 'preview' ? (
                            <>
                                <Button
                                    variant="outline-secondary"
                                    className="min100px"
                                    disabled={busy}
                                    onClick={handleBackToSources}
                                >
                                    {t(`${ns}.btnBack`)}
                                </Button>
                                <Button
                                    variant="info"
                                    className="min100px off-transfer-footer-btn"
                                    disabled={busy || !canApprove}
                                    onClick={() => void handleApprove()}
                                >
                                    {spinnerTitle(
                                        <>
                                            <SolarIcon name="box-arrow-in-down" className="fs-14 me-2" />
                                            {t(`${ns}.btnApprove`)}
                                        </>,
                                        approving,
                                        t(`${ns}.btnApprove`),
                                    )}
                                </Button>
                            </>
                        ) : null}

                        {step === 'result' ? (
                            <Button
                                variant="info"
                                className="min100px off-transfer-footer-btn"
                                onClick={handleHide}
                            >
                                {t(`${ns}.btnClose`)}
                            </Button>
                        ) : null}
                    </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default Proposal2Warehouse;
